import type { ComponentChildren, VNode } from 'preact';
import { cloneElement, isValidElement } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { Portal } from './Portal';
import { cn } from '@/lib/cn';
import type { Placement } from './Popover';

export interface TooltipProps {
  label: ComponentChildren;
  shortcut?: string;
  placement?: Placement;
  delay?: number;
  /** Desactiva el tooltip sin desmontar el wrapper: mantener el Tooltip en el
   *  árbol con disabled evita recrear el elemento hijo (y perder focus o un
   *  click en curso) cuando el tooltip solo aplica en uno de dos estados. */
  disabled?: boolean;
  children: VNode;
}

export function Tooltip({
  label,
  shortcut,
  placement = 'top',
  delay = 350,
  disabled = false,
  children,
}: TooltipProps) {
  const triggerRef = useRef<HTMLElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = () => {
    if (disabled) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(true), delay);
  };
  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    setOpen(false);
  };

  // Si se desactiva con el tooltip abierto (o con el timer armado), cerrarlo:
  // sin esto reaparecería solo al re-activarse, sin hover nuevo.
  useLayoutEffect(() => {
    if (!disabled) return;
    if (timer.current) clearTimeout(timer.current);
    setOpen(false);
  }, [disabled]);

  useLayoutEffect(() => {
    if (!open) return;
    const t = triggerRef.current;
    const tip = tipRef.current;
    if (!t || !tip) return;
    const a = t.getBoundingClientRect();
    const p = tip.getBoundingClientRect();
    const offset = 6;
    let top = 0;
    let left = 0;
    if (placement.startsWith('top')) {
      top = a.top - p.height - offset;
      left = a.left + (a.width - p.width) / 2;
    } else if (placement.startsWith('bottom')) {
      top = a.bottom + offset;
      left = a.left + (a.width - p.width) / 2;
    } else if (placement === 'left') {
      top = a.top + (a.height - p.height) / 2;
      left = a.left - p.width - offset;
    } else if (placement === 'right') {
      top = a.top + (a.height - p.height) / 2;
      left = a.right + offset;
    }
    setPos({ top: top + window.scrollY, left: left + window.scrollX });
  }, [open, placement]);

  if (!isValidElement(children)) return children;

  const childWithRef = cloneElement(children, {
    ref: triggerRef,
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: show,
    onBlur: hide,
  });

  return (
    <>
      {childWithRef}
      {open ? (
        <Portal layer="popover">
          <div
            ref={tipRef}
            role="tooltip"
            className={cn(
              'fixed pointer-events-none rounded-md border border-border-strong bg-text-strong text-surface px-2 py-1 text-2xs font-medium shadow-md animate-fade-in',
              'flex items-center gap-1.5',
            )}
            style={{
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            <span>{label}</span>
            {shortcut ? (
              <span className="ml-1 rounded-xs border border-current px-1 font-mono text-3xs opacity-70">
                {shortcut}
              </span>
            ) : null}
          </div>
        </Portal>
      ) : null}
    </>
  );
}
